import React from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  SportsSoccer,
  CalendarToday,
  BookmarkAdd,
  BookmarkRemove,
  Person,
} from "@mui/icons-material";
import colors from "../../design-system/colors";

const getMatchDate = (match) => {
  const rawDate = match.fechaISO || match.fecha;
  if (!rawDate) return null;

  const parsedDate = new Date(rawDate);
  if (Number.isNaN(parsedDate.getTime())) return null;

  const timeValue = typeof match.hora === "string" ? match.hora.trim() : "";
  const [hours, minutes] = timeValue.split(":").map(Number);

  if (Number.isFinite(hours) && Number.isFinite(minutes)) {
    parsedDate.setHours(hours, minutes, 0, 0);
  }

  return parsedDate;
};

/**
 * ScoutingMatchCard — Displays a match with mark/unmark action and related player names.
 *
 * Props:
 *  - match {Object} watchedMatch document
 *  - isWatched {boolean}
 *  - onToggle {Function} called with (match, isWatched)
 */
const ScoutingMatchCard = ({ match, isWatched = false, onToggle }) => {
  const { t, i18n } = useTranslation("scouting");
  const matchDate = getMatchDate(match);
  const dateLabel = matchDate
    ? matchDate.toLocaleDateString(i18n.language, {
        weekday: "short",
        day: "2-digit",
        month: "short",
      })
    : t("components.summaryCards.dateUnknown");
  const timeLabel = typeof match.hora === "string" && match.hora.trim()
    ? match.hora.trim()
    : t("matchCard.timeUnknown");
  const isPast = matchDate ? matchDate < new Date() : false;

  return (
      <Paper
        className="sc-saved-match"
        elevation={0}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <SportsSoccer
            sx={{
              color: isWatched ? colors.primary[600] : colors.neutral[400],
              fontSize: 28,
              flexShrink: 0,
            }}
          />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={650}>
              {match.equipoLocal || "—"} vs {match.equipoVisitante || "—"}
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                <CalendarToday sx={{ fontSize: 12, color: colors.neutral[500] }} />
                <Typography variant="caption" color={colors.neutral[600]}>
                  {dateLabel} · {timeLabel}
                </Typography>
              </Box>
              {match.jornada && (
                <Chip
                  label={`J${match.jornada}`}
                  size="small"
                  sx={{ height: 18, fontSize: 10, bgcolor: 'action.selected', color: 'text.primary' }}
                />
              )}
              {match.categoria && (
                <Chip
                  label={match.categoria}
                  size="small"
                  sx={{ height: 18, fontSize: 10, bgcolor: colors.primary[50], color: colors.primary[700] }}
                />
              )}
              {match.competicion && (
                <Chip
                  label={match.competicion}
                  size="small"
                  sx={{ height: 18, fontSize: 10, bgcolor: 'action.selected', color: 'text.primary' }}
                />
              )}
            </Box>

            {match.reason && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, mt: 0.5 }}>
                <Person sx={{ fontSize: 12, color: colors.primary[500] }} />
                <Typography variant="caption" color={colors.primary[700]}>
                  {match.reason}
                </Typography>
              </Box>
            )}

            {isPast && (
              <Chip
                label={t("workspace.pastMatch")}
                size="small"
                sx={{
                  mt: 0.5,
                  height: 18,
                  fontSize: 10,
                  bgcolor: colors.neutral[200],
                  color: colors.neutral[600],
                }}
              />
            )}
          </Box>

          <Tooltip title={isWatched ? t("workspace.removeFromAgenda") : t("matchCard.addToAgenda")}>
            <IconButton
              size="small"
              onClick={() => onToggle(match, isWatched)}
              aria-label={isWatched ? t("workspace.removeFromAgenda") : t("matchCard.addToAgenda")}
              sx={{
                color: isWatched ? colors.primary[600] : colors.neutral[400],
                "&:hover": { color: colors.primary[700] },
              }}
            >
              {isWatched ? (
                <BookmarkRemove fontSize="small" />
              ) : (
                <BookmarkAdd fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
  );
};

export default ScoutingMatchCard;
